import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, useAuth } from '../store/auth.store'
import { useImpersonation } from '../store/impersonation.store'
import type { Alert, PaginatedResponse } from '../types'

const LIST_REFETCH_MS = 30_000

export function useAlerts(params?: { page?: number; limit?: number; status?: string; severity?: string; type?: string; search?: string }) {
  const api = useApi()
  const { impersonateMailboxId } = useImpersonation()
  const { user } = useAuth()
  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set('page', String(params.page))
  if (params?.limit) searchParams.set('limit', String(params.limit))
  if (params?.status) searchParams.set('status', params.status)
  if (params?.severity) searchParams.set('severity', params.severity)
  if (params?.type) searchParams.set('type', params.type)
  if (params?.search) searchParams.set('search', params.search)
  if (impersonateMailboxId) searchParams.set('mailboxId', impersonateMailboxId)
  const qs = searchParams.toString()
  return useQuery<PaginatedResponse<Alert>>({
    queryKey: ['alerts', user?.id, params, impersonateMailboxId],
    queryFn: () => api.get<PaginatedResponse<Alert>>(`/alerts${qs ? `?${qs}` : ''}`),
    refetchInterval: LIST_REFETCH_MS,
    refetchIntervalInBackground: false,
  })
}

export function useAlertStats() {
  const api = useApi()
  const { impersonateMailboxId } = useImpersonation()
  const { user } = useAuth()
  const qs = impersonateMailboxId ? `?mailboxId=${impersonateMailboxId}` : ''
  return useQuery<{
    total: number
    byStatus: Record<string, number>
    bySeverity: Record<string, number>
    byType: Record<string, number>
  }>({
    queryKey: ['alertStats', user?.id, impersonateMailboxId],
    queryFn: () => api.get(`/alerts/stats${qs}`),
    refetchInterval: LIST_REFETCH_MS,
    refetchIntervalInBackground: false,
  })
}

export function useAlert(id: string) {
  const api = useApi()
  return useQuery<Alert>({
    queryKey: ['alert', id],
    queryFn: () => api.get<Alert>(`/alerts/${id}`),
    enabled: !!id,
  })
}

export function useAcknowledgeAlert() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.patch(`/alerts/${id}/acknowledge`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alerts'] })
      qc.invalidateQueries({ queryKey: ['alertStats'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useResolveAlert() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      api.patch(`/alerts/${id}/resolve`, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alerts'] })
      qc.invalidateQueries({ queryKey: ['alertStats'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}
